import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rewardAPI, userAPI } from '../services/api';
import { Gift, Award, ShoppingBag, Star } from 'lucide-react';
import Pagination from '../components/Pagination';
import { SkeletonGrid, SkeletonTable } from '../components/LoadingSkeleton';
import './Rewards.css';

const typeIcons = {
  merchandise: '🛍️',
  gift_card: '🎁',
  experience: '✨',
  donation: '💝',
  other: '🎯',
};

function Rewards() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('catalog');
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(20);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userAPI.getProfile().then(res => res.data.user)
  });

  const { data: rewards, isLoading } = useQuery({
    queryKey: ['rewards'],
    queryFn: () => rewardAPI.getAll().then(res => res.data.rewards)
  });

  const { data: redemptionsData, isLoading: redemptionsLoading } = useQuery({
    queryKey: ['redemptions', historyPage, historyLimit],
    queryFn: () => rewardAPI.getRedemptions({ page: historyPage, limit: historyLimit }).then(res => res.data),
    enabled: tab === 'history',
    keepPreviousData: true,
  });
  const redemptions = redemptionsData?.redemptions || [];

  const redeemMutation = useMutation({
    mutationFn: (id) => rewardAPI.redeem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
      queryClient.invalidateQueries({ queryKey: ['redemptions'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setRedeemTarget(null);
    }
  });

  const userPoints = profile?.points || 0;

  return (
    <div className="rewards-page">
      <div className="rewards-header">
        <h1><Gift size={28} /> Rewards</h1>
        <div className="points-balance">
          <Award size={20} />
          <span className="balance-amount">{userPoints}</span> pts available
        </div>
      </div>

      <div className="rewards-tabs">
        <button className={`rewards-tab ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>
          <ShoppingBag size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          Catalog
        </button>
        <button className={`rewards-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          <Star size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          My Redemptions
        </button>
      </div>

      {tab === 'catalog' && (
        <>
          {isLoading ? (
            <SkeletonGrid count={6} />
          ) : !rewards || rewards.length === 0 ? (
            <div className="rewards-empty">
              <Gift size={48} color="#d1d5db" />
              <h3>No rewards available yet</h3>
              <p>Check back soon — new rewards are added regularly.</p>
            </div>
          ) : (
            <div className="rewards-grid">
              {rewards.map(reward => {
                const canAfford = userPoints >= reward.points_cost;
                const lowStock = reward.stock_quantity !== null && reward.stock_quantity > 0 && reward.stock_quantity <= 5;
                const outOfStock = reward.stock_quantity !== null && reward.stock_quantity <= 0;

                return (
                  <div key={reward.id} className="reward-card">
                    <div className="reward-image">
                      {reward.image_url ? (
                        <img src={reward.image_url} alt={reward.title} />
                      ) : (
                        <span>{typeIcons[reward.type] || '🎯'}</span>
                      )}
                    </div>
                    <div className="reward-body">
                      <span className={`reward-type ${reward.type}`}>{reward.type?.replace('_', ' ')}</span>
                      <h3>{reward.title}</h3>
                      {reward.description && <p className="reward-desc">{reward.description}</p>}
                      <div className="reward-footer">
                        <div className="reward-cost">
                          {reward.points_cost} <span>pts</span>
                        </div>
                        {reward.stock_quantity !== null && (
                          <span className={`reward-stock ${lowStock ? 'low' : ''}`}>
                            {outOfStock ? 'Out of stock' : `${reward.stock_quantity} left`}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn-redeem"
                        disabled={!canAfford || outOfStock}
                        onClick={() => setRedeemTarget(reward)}
                      >
                        {outOfStock ? 'Out of Stock' : !canAfford ? `Need ${reward.points_cost - userPoints} more pts` : 'Redeem'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <div className="redemptions-container">
          <h2>Redemption History</h2>
          {redemptionsLoading ? (
            <SkeletonTable rows={4} columns={4} />
          ) : !redemptions || redemptions.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
              You haven't redeemed any rewards yet.
            </p>
          ) : (
            <table className="redemptions-table">
              <thead>
                <tr>
                  <th>Reward</th>
                  <th>Points</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map(r => (
                  <tr key={r.id}>
                    <td>{r.reward_title}</td>
                    <td>{r.points_spent}</td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td><span className={`redemption-status ${r.status}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {redemptionsData?.pagination && (
            <Pagination
              page={historyPage}
              totalPages={redemptionsData.pagination.total_pages}
              total={redemptionsData.pagination.total}
              limit={historyLimit}
              onPageChange={setHistoryPage}
              onLimitChange={(l) => { setHistoryLimit(l); setHistoryPage(1); }}
            />
          )}
        </div>
      )}

      {/* Confirm redeem modal */}
      {redeemTarget && (
        <div className="redeem-overlay" onClick={() => setRedeemTarget(null)}>
          <div className="redeem-modal" onClick={e => e.stopPropagation()}>
            <h3>Redeem {redeemTarget.title}?</h3>
            <div className="redeem-cost-display">{redeemTarget.points_cost} pts</div>
            <p>Your balance: <strong>{userPoints} pts</strong></p>
            <p>After redemption: <strong>{userPoints - redeemTarget.points_cost} pts</strong></p>
            {redeemMutation.isError && (
              <p style={{ color: '#dc2626', marginTop: 12 }}>
                {redeemMutation.error?.response?.data?.error || 'Redemption failed. Please try again.'}
              </p>
            )}
            <div className="redeem-actions">
              <button className="btn-close" onClick={() => setRedeemTarget(null)}>Cancel</button>
              <button
                className="btn-redeem"
                style={{ width: 'auto', padding: '10px 24px' }}
                disabled={redeemMutation.isPending}
                onClick={() => redeemMutation.mutate(redeemTarget.id)}
              >
                {redeemMutation.isPending ? 'Redeeming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rewards;
